import { ArrowRight } from "@tamagui/lucide-icons";
import { useEffect } from "react";
import { type Control, Controller, useForm } from "react-hook-form";
import { YStack } from "tamagui";

import { accessTrackName } from "@/access/tracks";
import {
  ConsentRequest,
  ConsentType,
  UserProfileResponse,
} from "@/api/generated/models";
import { useUpdateConsents } from "@/api/mutations/consent";
import { useGetUserProfile } from "@/api/queries/userProfile";
import {
  Button,
  Checkbox,
  Footer,
  Header,
  Heading,
  Link,
  Paragraph,
  ScrollView,
  Skeleton,
  Text,
} from "@/components";
import { useFeatureFlag } from "@/components/FeatureFlagProvider";
import { medicalGroupName, shortBrandName } from "@/config/brand";
import { useUserProgression } from "@/conversation/sidebar/UserProgression/UserProgressionContext";

type ConsentRequestConsents = ConsentRequest["consents"];
type UserProfileResponseConsents = UserProfileResponse["consents"];

export const CLOUD_HEALTH_AOB_URL =
  "https://cdn.prod.website-files.com/619acca858666ac6759d6e31/6a6ce21cf0f554d34ac3218b_cloudHealth_AssignmentOfBenefitsMA_20250605.pdf";

const REQUIRED_CONSENT_TYPES_BASE = [
  ConsentType.telehealth_informed_consent,
  ConsentType.terms_of_use_and_privacy,
  ConsentType.data_sharing,
] as const;

const requiredConsentTypes = (
  showAobCheckbox: boolean,
): readonly ConsentType[] =>
  showAobCheckbox
    ? [...REQUIRED_CONSENT_TYPES_BASE, ConsentType.bridge_insurance_consent]
    : REQUIRED_CONSENT_TYPES_BASE;

export const allConsentsChecked = (
  consents: UserProfileResponseConsents,
  showAobCheckbox = false,
) =>
  requiredConsentTypes(showAobCheckbox).every((consent) => !!consents[consent]);

type ConsentFormData = {
  [ConsentType.telehealth_informed_consent]: boolean;
  // Backend tracks one field but UI splits terms and privacy into 2 consent checkboxes.
  termsConsent: boolean;
  privacyConsent: boolean;
  [ConsentType.data_sharing]: boolean;
  [ConsentType.bridge_insurance_consent]: boolean;
  // Optional consents (HIE + Surescripts).
  [ConsentType.hie_consent]: boolean;
  [ConsentType.surescripts_consent]: boolean;
};

type ConsentItem = {
  name: keyof ConsentFormData;
  label: React.ReactNode;
};

const CHMG_CONSENT_TYPES_PRE_BRIDGE = [
  {
    name: ConsentType.telehealth_informed_consent,
    label: (
      <Paragraph size="$sm" lineHeight="$md">
        I agree to CHMG's{" "}
        <Link
          size="$sm"
          lineHeight="$md"
          external
          href="https://www.curaihealth.com/informed-consent"
          bold
        >
          Telehealth Informed Consent
        </Link>
        .
      </Paragraph>
    ),
  },
  {
    name: "termsConsent",
    label: (
      <Paragraph size="$sm" lineHeight="$md">
        I agree to CHMG's{" "}
        <Link
          size="$sm"
          lineHeight="$md"
          href="https://www.curaihealth.com/legal#tc"
          external
          bold
        >
          Terms of Use
        </Link>
        .
      </Paragraph>
    ),
  },
] satisfies ConsentItem[];

const BRIDGE_CONSENT_TYPES = [
  {
    name: ConsentType.bridge_insurance_consent,
    label: (
      <Paragraph size="$sm" lineHeight="$md">
        I agree to Cloud Health{" "}
        <Link
          size="$sm"
          lineHeight="$md"
          href={CLOUD_HEALTH_AOB_URL}
          external
          bold
        >
          Assignment of Benefits
        </Link>
        .
      </Paragraph>
    ),
  },
] satisfies ConsentItem[];

const CHMG_CONSENT_TYPES_POST_BRIDGE = [
  {
    name: "privacyConsent",
    label: (
      <Paragraph size="$sm" lineHeight="$md">
        I acknowledge CHMG's{" "}
        <Link
          size="$sm"
          lineHeight="$md"
          href="https://www.curaihealth.com/legal#privacy"
          external
          bold
        >
          Privacy Policies
        </Link>{" "}
        and{" "}
        <Link
          size="$sm"
          lineHeight="$md"
          href="https://www.curaihealth.com/legal#notice"
          external
          bold
        >
          Privacy Practices
        </Link>
        .
      </Paragraph>
    ),
  },
  {
    name: ConsentType.data_sharing,
    label: (
      <Paragraph size="$sm" lineHeight="$md">
        I agree to Curai Health & CHMG sharing my profile information, messages,
        care plans, lab and medication orders, lab results, and doctor notes for
        this visit and future care.
      </Paragraph>
    ),
  },
] satisfies ConsentItem[];

// Optional consents (HIE + Surescripts) — always rendered.
const HIE_SURESCRIPTS_CONSENT_TYPES = [
  {
    name: ConsentType.hie_consent,
    label: (
      <Paragraph size="$sm" lineHeight="$md">
        I consent to sharing my health records through the Health Information
        Exchange (HIE) network to support my care.
      </Paragraph>
    ),
  },
  {
    name: ConsentType.surescripts_consent,
    label: (
      <Paragraph size="$sm" lineHeight="$md">
        I agree to Curai Health pulling relevant medical data via{" "}
        <Link size="$sm" external semibold href="https://www.surescripts.com/">
          Surescripts
        </Link>{" "}
        to expedite my care.
      </Paragraph>
    ),
  },
] satisfies ConsentItem[];

const consentFormDataFromConsents = (
  consents: UserProfileResponseConsents,
): ConsentFormData => {
  const termsAndPrivacy = !!consents[ConsentType.terms_of_use_and_privacy];

  return {
    [ConsentType.telehealth_informed_consent]:
      !!consents[ConsentType.telehealth_informed_consent],
    termsConsent: termsAndPrivacy,
    privacyConsent: termsAndPrivacy,
    [ConsentType.data_sharing]: !!consents[ConsentType.data_sharing],
    [ConsentType.bridge_insurance_consent]:
      !!consents[ConsentType.bridge_insurance_consent],
    [ConsentType.hie_consent]: !!consents[ConsentType.hie_consent],
    [ConsentType.surescripts_consent]:
      !!consents[ConsentType.surescripts_consent],
  };
};

const missingRequiredConsents = (
  formData: ConsentFormData,
  showAobCheckbox: boolean,
) => {
  const items: ConsentItem[] = [
    ...CHMG_CONSENT_TYPES_PRE_BRIDGE,
    ...(showAobCheckbox ? BRIDGE_CONSENT_TYPES : []),
    ...CHMG_CONSENT_TYPES_POST_BRIDGE,
  ];
  return items.filter((item) => !formData[item.name]).map((item) => item.name);
};

const consentRequestFromFormData = (
  formData: ConsentFormData,
  bridgeInsuranceConsent: boolean | null,
): ConsentRequestConsents => ({
  [ConsentType.telehealth_informed_consent]:
    formData[ConsentType.telehealth_informed_consent],
  [ConsentType.terms_of_use_and_privacy]:
    formData.termsConsent && formData.privacyConsent,
  [ConsentType.data_sharing]: formData[ConsentType.data_sharing],
  ...(bridgeInsuranceConsent === null
    ? {}
    : { [ConsentType.bridge_insurance_consent]: bridgeInsuranceConsent }),
  // Optional consents: only persist when granted.
  ...(formData[ConsentType.hie_consent]
    ? { [ConsentType.hie_consent]: true }
    : {}),
  ...(formData[ConsentType.surescripts_consent]
    ? { [ConsentType.surescripts_consent]: true }
    : {}),
});

/**
 * The AOB consent value to submit, or null to leave it untouched.
 *
 * `insurance-pay` off means there are no benefits to assign.
 * With it on and `aob-checkbox` off, the AOB is rolled into the CHMG consents and accepting those
 * grants it for every patient, not just insurance payers. With `aob-checkbox` on it's a separate
 * box, so only the patients we actually showed the checkbox to can grant it.
 */
const resolveBridgeConsent = (
  formData: ConsentFormData,
  flags: {
    isInsurancePayEnabled: boolean;
    isAobCheckboxEnabled: boolean;
    showAobCheckbox: boolean;
  },
): boolean | null => {
  if (!flags.isInsurancePayEnabled) return null;
  if (!flags.isAobCheckboxEnabled) return true;
  return flags.showAobCheckbox
    ? formData[ConsentType.bridge_insurance_consent]
    : null;
};

// Terms and Privacy are shown as two checkboxes but map to the single
// `terms_of_use_and_privacy` backend consent
const isConsentAlreadyGiven = (
  name: keyof ConsentFormData,
  consents: UserProfileResponseConsents,
): boolean =>
  name === "termsConsent" || name === "privacyConsent"
    ? !!consents[ConsentType.terms_of_use_and_privacy]
    : !!consents[name as ConsentType];

const hasConsentChanges = (
  requestedConsents: ConsentRequestConsents,
  existingConsents: UserProfileResponseConsents,
) =>
  Object.entries(requestedConsents).some(
    ([key, value]) => existingConsents[key as ConsentType] !== value,
  );

const HieSurescriptsConsentChecks = ({
  control,
  existingConsents,
}: {
  control: Control<ConsentFormData>;
  existingConsents: UserProfileResponseConsents;
}) => (
  <>
    {HIE_SURESCRIPTS_CONSENT_TYPES.filter(
      (item) => !isConsentAlreadyGiven(item.name, existingConsents),
    ).map((item) => (
      <YStack key={item.name} gap="$200" paddingBottom="$200">
        <Controller
          control={control}
          name={item.name}
          render={({ field, fieldState }) => (
            <Checkbox
              ref={field.ref}
              id={item.name}
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
              errorText={fieldState.error?.message}
              label={
                <Checkbox.Label htmlFor={item.name}>
                  {item.label}
                </Checkbox.Label>
              }
            />
          )}
        />
      </YStack>
    ))}
  </>
);

const ConsentChecks = ({
  control,
  existingConsents,
  isLoading,
  showAobCheckbox,
}: {
  control: Control<ConsentFormData>;
  existingConsents: UserProfileResponseConsents;
  isLoading: boolean;
  showAobCheckbox: boolean;
}) => {
  if (isLoading) {
    return (
      <YStack marginTop="$200" gap="$150">
        <Skeleton height="$300" />
        <Skeleton height="$300" />
        <Skeleton height="$300" />
      </YStack>
    );
  }

  // Only surface consents the user hasn't already given
  const items: ConsentItem[] = [
    ...CHMG_CONSENT_TYPES_PRE_BRIDGE,
    ...(showAobCheckbox ? BRIDGE_CONSENT_TYPES : []),
    ...CHMG_CONSENT_TYPES_POST_BRIDGE,
  ].filter((item) => !isConsentAlreadyGiven(item.name, existingConsents));

  if (items.length === 0) {
    return (
      <Text size="$sm" lineHeight="$sm" color="$textSecondary">
        {`You previously consented to data sharing between ${medicalGroupName} and ${shortBrandName}. To revoke this go to Settings → Data Sharing. You can manage your Health Information Exchange (HIE) preferences in Settings at any time.`}
      </Text>
    );
  }

  return (
    <YStack gap="$200" paddingBottom="$200">
      {items.map((item) => (
        <Controller
          key={item.name}
          control={control}
          name={item.name}
          render={({ field, fieldState }) => (
            <Checkbox
              ref={field.ref}
              id={item.name}
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
              errorText={fieldState.error?.message}
              label={
                <Checkbox.Label htmlFor={item.name}>
                  {item.label}
                </Checkbox.Label>
              }
            />
          )}
        />
      ))}
    </YStack>
  );
};

export default function CuraiConsentScreen({
  onContinue,
  isSubmitting = false,
  loading = false,
}: {
  onContinue: () => void;
  isSubmitting?: boolean;
  loading?: boolean;
}) {
  const { data, isLoading } = useGetUserProfile({
    refetchOnWindowFocus: true,
  });
  const { value: isInsurancePayEnabled } = useFeatureFlag(
    "insurance-pay",
    false,
  );
  const { value: isAobCheckboxEnabled, loading: isAobFlagLoading } =
    useFeatureFlag("aob-checkbox", false);
  const progression = useUserProgression()?.progression;
  const showAobCheckbox =
    isAobCheckboxEnabled &&
    isInsurancePayEnabled &&
    progression?.metadata?.payment_mode === "insurance";
  const requireHie =
    accessTrackName(progression?.flow_name ?? "") !== undefined;

  const isConsentsLoading = isLoading || isAobFlagLoading;
  const { mutate: updateConsents, isPending: isUpdateConsentsPending } =
    useUpdateConsents();
  const isBusy = isUpdateConsentsPending || isConsentsLoading || loading;

  const existingConsents = data?.consents || {};
  const existingAllChecked = allConsentsChecked(
    existingConsents,
    showAobCheckbox,
  );

  const { control, handleSubmit, reset, setError } = useForm<ConsentFormData>({
    defaultValues: consentFormDataFromConsents({}),
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!data?.consents) return;
    reset(consentFormDataFromConsents(data.consents), {
      keepFieldsRef: true,
    });
  }, [data?.consents, reset]);

  const onSubmit = handleSubmit((formData) => {
    const missingConsents = existingAllChecked
      ? []
      : missingRequiredConsents(formData, showAobCheckbox);
    if (requireHie && !formData[ConsentType.hie_consent]) {
      missingConsents.push(ConsentType.hie_consent);
    }
    for (const [index, name] of missingConsents.entries()) {
      setError(
        name,
        { message: "Required to continue" },
        { shouldFocus: index === 0 },
      );
    }
    if (missingConsents.length > 0) return;

    const newConsents = consentRequestFromFormData(
      formData,
      resolveBridgeConsent(formData, {
        isInsurancePayEnabled,
        isAobCheckboxEnabled,
        showAobCheckbox,
      }),
    );

    // All consents are checked - save new ones if any, then continue
    if (hasConsentChanges(newConsents, existingConsents)) {
      updateConsents(
        { data: { consents: newConsents } },
        { onSuccess: () => onContinue() },
      );
    } else {
      onContinue();
    }
  });

  const stickyFooter = (
    <Footer gap="$100" padding="$200" mode="dialog">
      <Button
        dataTrackingId="curai-consent-confirm-and-continue-button"
        mode="primary"
        constrained
        onPress={onSubmit}
        width="100%"
        iconAfter={ArrowRight}
        disabled={isBusy}
        loading={isBusy}
      >
        {isSubmitting ? "Beginning visit..." : "Confirm & continue"}
      </Button>
    </Footer>
  );

  return (
    <>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          padding: "$250",
        }}
      >
        <Header gap="$150" marginBottom="$250">
          <Heading size="$lg">Consent & data sharing</Heading>
          <Paragraph size="$sm" lineHeight="$md" color="$textSecondary">
            {`Because you are now connecting with ${medicalGroupName} (CHMG), you'll need to consent to their Terms & Privacy Policy, as well as consent to treat.`}
          </Paragraph>
        </Header>
        <ConsentChecks
          control={control}
          existingConsents={existingConsents}
          isLoading={isConsentsLoading}
          showAobCheckbox={showAobCheckbox}
        />
        {!isConsentsLoading && (
          <HieSurescriptsConsentChecks
            control={control}
            existingConsents={existingConsents}
          />
        )}
      </ScrollView>
      {stickyFooter}
    </>
  );
}
